import "reflect-metadata";
import {
  IsBoolean,
  IsDate,
  IsEnum,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID
} from "class-validator";

import { ESubscriptionStatus } from "@/models/subscriptions/status";
import { ETestingAssignmentStatus } from "@/models/testing/assignment";
import { ETaskCategory, ETaskStatus } from "@/models/task/task";

export enum ESkillChangeReason {
  MANUAL = 1, //  Ручное изменение
  PROGRAM = 2, // Прохождение программы
  TASK = 3, // Индивидуальное/практическое задание
  TESTING = 4, // Тестирование
  PRIORITY = 5, // Изменение приоритета
  AKT = 6 // Завершение ЕПЗ
}

export interface IAnalytics {
  // Общие (матрица навыков)
  readonly id: string;
  readonly workplace_id?: string;
  readonly identification_number?: string;
  readonly full_name?: string;
  readonly factory_name?: string;
  readonly infinity_name?: string;
  readonly position_name?: string;
  readonly user_roles?: string;
  readonly supervisor_name?: string;
  readonly user_signs?: string;
  readonly block_name?: string;
  readonly skill_name?: string;
  readonly target_skill_id?: string;
  readonly target_level?: number;
  readonly current_level?: number;
  readonly current_level_reason_id?: ESkillChangeReason;
  readonly current_level_updater_full_name?: string;
  readonly current_level_created_at?: string;
  readonly pillar_name?: string;
  readonly category_name?: string;
  // ИПР
  readonly program_title?: string;
  readonly inspectors_names?: string;
  readonly is_self_education?: string;
  readonly program_days_to_study?: number;
  readonly program_starts_at?: string;
  readonly program_ends_at?: string;
  readonly mentor_name?: string;
  readonly subscription_status?: string;
  // Тест
  readonly testing_name?: string;
  readonly testing_assignment_status?: string;
  readonly testing_assignment_starts_at?: string;
  readonly testing_assignment_ends_at?: string;
  readonly testing_assignment_real_ends_at?: string;
  // Индивидуальные/Практические задания
  readonly task_description?: string;
  readonly task_status?: string;
  readonly task_category?: string;
  readonly task_created_at?: string;
  readonly task_ends_at?: string;
  readonly responsible_name?: string;
  readonly task_comment?: string;
  // Внешнее обучение
  readonly document_name?: string;
  readonly document_provider?: string;
  readonly document_price?: string;
  readonly document_start_education_at?: string;
  readonly document_end_education_at?: string;
  // Внеплановое обучение
  readonly manual_program_title?: string;
  readonly manual_program_assignment_level?: string;
  readonly manual_program_inspectors_names?: string;
  readonly manual_program_is_self_education?: string;
  readonly manual_program_days_to_study?: number;
  readonly manual_program_starts_at?: string;
  readonly manual_program_ends_at?: string;
  readonly manual_program_mentor_name?: string;
  readonly manual_program_subscription_status?: string;
}

export enum EAnalyticsFilterType {
  SKILL_MATRIX = 1,
  INDIVIDUAL_DEVELOPMENT_PLAN = 3,
  TESTING = 4,
  INDIVIDUAL_PRACTICAL_TASK = 4,
  EXTERNAL_EDUCATION = 5
}

export default class AnalyticsFilter {
  @IsUUID("4")
  public id!: string;

  @IsOptional()
  @IsString()
  public name?: string;

  @IsEnum(EAnalyticsFilterType)
  public type_id!: EAnalyticsFilterType;

  @IsOptional()
  @IsUUID("4")
  public user_id?: string;

  @IsOptional()
  @IsUUID("4")
  public template_id?: string;

  @IsBoolean()
  public only_problem_skills: boolean = false;

  /**
   * ! Общие (матрица навыков)
   */

  @IsBoolean()
  public show_identification_number: boolean = false;

  @IsOptional()
  @IsString()
  public identification_number: string | null = null;

  @IsBoolean()
  public show_full_name: boolean = false;

  @IsOptional()
  @IsString()
  public full_name: string | null = null;

  @IsBoolean()
  public show_workplace: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public factory_id: string | null = null;

  @IsOptional()
  @IsUUID("4")
  public infinity_id: string | null = null;

  @IsBoolean()
  public show_position: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public position_id: string | null = null;

  @IsBoolean()
  public show_role: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public role_id: string | null = null;

  @IsBoolean()
  public show_supervisor: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public supervisor_id: string | null = null;

  @IsBoolean()
  public show_user_sign: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public sign_id: string | null = null;

  @IsBoolean()
  public show_block: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public block_id: string | null = null;

  @IsBoolean()
  public show_skill: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public skill_id: string | null = null;

  @IsBoolean()
  public show_target_skill_level: boolean = false;

  @IsBoolean()
  public show_current_skill_level: boolean = false;

  @IsBoolean()
  public show_current_skill_changed_reason: boolean = false;

  @IsBoolean()
  public show_current_skill_changed_by: boolean = false;

  @IsBoolean()
  public show_current_skill_changed_at: boolean = false;

  @IsBoolean()
  public show_pillar: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public pillar_id: string | null = null;

  @IsBoolean()
  public show_skill_category: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public category_id: string | null = null;

  /**
   * ! Общие фильтры для ИПР и Внепланового обучения
   */

  @IsOptional()
  @IsUUID("4")
  public program_id: string | null = null;

  @IsOptional()
  @IsUUID("4")
  public inspector_id: string | null = null;

  @IsOptional()
  @IsBoolean()
  public is_self_education_filter?: boolean | null = null;

  @IsOptional()
  @IsDate()
  public program_starts_at: Date | null = null;

  @IsOptional()
  @IsDate()
  public program_ends_at: Date | null = null;

  @IsOptional()
  @IsNumber()
  public education_count_days: number | null = null;

  @IsOptional()
  @IsUUID("4")
  public mentor_id: string | null = null;

  @IsOptional()
  @IsEnum(ESubscriptionStatus)
  public subscription_status?: ESubscriptionStatus | null = null;

  /**
   * ! ИПР
   */

  @IsBoolean()
  public show_subscription_program: boolean = false;

  @IsBoolean()
  public show_subscription_program_inspector: boolean = false;

  @IsBoolean()
  public show_is_self_education: boolean = false;

  @IsBoolean()
  public show_education_count_days: boolean = false;

  @IsBoolean()
  public show_program_interval: boolean = false;

  @IsBoolean()
  public show_mentor: boolean = false;

  @IsBoolean()
  public show_subscription_status: boolean = false;

  /**
   * ! Тест
   */

  @IsBoolean()
  public show_testing: boolean = false;

  @IsOptional()
  @IsUUID("4")
  public testing_id: string | null = null;

  @IsBoolean()
  public show_testing_assignment_status: boolean = false;

  @IsOptional()
  @IsEnum(ETestingAssignmentStatus)
  public testing_assignment_status: ETestingAssignmentStatus | null = null;

  @IsBoolean()
  public show_testing_assignment_starts_at: boolean = false;

  @IsBoolean()
  public show_testing_assignment_ends_at: boolean = false;

  @IsBoolean()
  public show_testing_assignment_real_ends_at: boolean = false;

  /**
   * ! Индивидуальные/Практические задания
   */

  @IsBoolean()
  public show_task_description: boolean = false;

  @IsBoolean()
  public show_task_status: boolean = false;

  @IsOptional()
  @IsEnum(ETaskStatus)
  public task_status: ETaskStatus | null = null;

  @IsBoolean()
  public show_task_category: boolean = false;

  @IsOptional()
  @IsEnum(ETaskCategory)
  public task_category: ETaskCategory | null = null;

  @IsBoolean()
  public show_task_created_at: boolean = false;

  @IsBoolean()
  public show_task_ends_at: boolean = false;

  @IsBoolean()
  public show_task_responsible: boolean = false;

  @IsOptional()
  @IsString()
  public responsible_name: string | null = null;

  @IsBoolean()
  public show_task_comment: boolean = false;

  /**
   * ! Внешнее обучение
   */

  @IsBoolean()
  public show_external_title: boolean = false;

  @IsBoolean()
  public show_external_provider: boolean = false;

  @IsBoolean()
  public show_external_cost: boolean = false;

  @IsBoolean()
  public show_external_starts_at: boolean = false;

  @IsBoolean()
  public show_external_ends_at: boolean = false;

  /**
   * ! Внеплановое обучение
   */

  @IsBoolean()
  public show_program_assignment: boolean = false;

  @IsBoolean()
  public show_program_assignment_level: boolean = false;

  @IsBoolean()
  public show_program_assignment_inspector: boolean = false;

  @IsBoolean()
  public show_program_assignment_is_self_education: boolean = false;

  @IsBoolean()
  public show_program_assignment_education_count_days: boolean = false;

  @IsBoolean()
  public show_program_assignment_interval: boolean = false;

  @IsBoolean()
  public show_program_assignment_mentor: boolean = false;

  @IsBoolean()
  public show_program_assignment_subscription_status: boolean = false;

  public get show_skills_info() {
    return (
      this.show_block ||
      this.show_skill ||
      this.show_target_skill_level ||
      this.show_current_skill_level ||
      this.show_current_skill_changed_reason ||
      this.show_current_skill_changed_by ||
      this.show_current_skill_changed_at ||
      this.show_pillar ||
      this.show_skill_category ||
      this.only_problem_skills
    );
  }

  public get show_programs_info() {
    return (
      this.show_subscription_program ||
      this.show_subscription_program_inspector ||
      this.show_is_self_education ||
      this.show_education_count_days ||
      this.show_program_interval ||
      this.show_mentor ||
      this.show_subscription_status
    );
  }

  public get show_testing_info() {
    return (
      this.show_testing ||
      this.show_testing_assignment_status ||
      this.show_testing_assignment_starts_at ||
      this.show_testing_assignment_ends_at ||
      this.show_testing_assignment_real_ends_at
    );
  }

  public get show_task_info() {
    return (
      this.show_task_description ||
      this.show_task_status ||
      this.show_task_category ||
      this.show_task_created_at ||
      this.show_task_ends_at ||
      this.show_task_responsible ||
      this.show_task_comment
    );
  }

  public get show_external_info() {
    return (
      this.show_external_title ||
      this.show_external_provider ||
      this.show_external_cost ||
      this.show_external_starts_at ||
      this.show_external_ends_at
    );
  }

  public get show_manual_program_assignment_info() {
    return (
      this.show_program_assignment ||
      this.show_program_assignment_level ||
      this.show_program_assignment_inspector ||
      this.show_program_assignment_is_self_education ||
      this.show_program_assignment_education_count_days ||
      this.show_program_assignment_interval ||
      this.show_program_assignment_mentor ||
      this.show_program_assignment_subscription_status
    );
  }

  public get disable_skills_checkboxes() {
    return this.show_testing_info || this.show_external_info;
  }

  public get disable_skills_checkboxes_v2() {
    return this.show_manual_program_assignment_info;
  }

  public get disable_program_checkboxes() {
    return (
      this.show_testing_info ||
      this.show_task_info ||
      this.show_external_info ||
      this.show_manual_program_assignment_info
    );
  }

  public get disable_testing_checkboxes() {
    return (
      this.show_skills_info ||
      this.show_programs_info ||
      this.show_task_info ||
      this.show_external_info ||
      this.show_manual_program_assignment_info
    );
  }

  public get disable_task_checkboxes() {
    return (
      this.show_programs_info ||
      this.show_testing_info ||
      this.show_external_info ||
      this.show_manual_program_assignment_info
    );
  }

  public get disable_external_checkboxes() {
    return (
      this.show_skills_info ||
      this.show_programs_info ||
      this.show_testing_info ||
      this.show_task_info ||
      this.show_manual_program_assignment_info
    );
  }

  public get disable_manual_program_assignment_checkboxes() {
    return (
      this.show_programs_info ||
      this.show_testing_info ||
      this.show_task_info ||
      this.show_external_info ||
      this.show_target_skill_level ||
      this.show_current_skill_level ||
      this.show_current_skill_changed_reason ||
      this.show_current_skill_changed_by ||
      this.show_current_skill_changed_at
    );
  }
}
